'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuoteFormData {
  moveType: string;
  moveDate: string;
  fromLocation: string;
  toLocation: string;
  propertySize: string;
  packingService: boolean;
  assemblyService: boolean;
  storageService: boolean;
  specialItems: string[];
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

interface QuoteFormProps {
  onQuoteGenerated: (quote: QuoteFormData & { estimatedPrice: number; distance: number }) => void;
}

const QuoteForm = ({ onQuoteGenerated }: QuoteFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<QuoteFormData>({
    moveType: '',
    moveDate: '',
    fromLocation: '',
    toLocation: '',
    propertySize: '',
    packingService: false,
    assemblyService: false,
    storageService: false,
    specialItems: [],
    contactName: '',
    contactPhone: '',
    contactEmail: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof QuoteFormData, string>>>({});
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const moveTypes = [
    { value: 'residential', label: 'Residential Move', icon: 'HomeIcon' },
    { value: 'commercial', label: 'Commercial Move', icon: 'BuildingOfficeIcon' },
    { value: 'storage', label: 'Storage Only', icon: 'ArchiveBoxIcon' },
  ];

  const propertySizes = [
    { value: 'studio', label: 'Studio/1BR', multiplier: 1 },
    { value: '2br', label: '2 Bedroom', multiplier: 1.5 },
    { value: '3br', label: '3 Bedroom', multiplier: 2 },
    { value: '4br', label: '4+ Bedroom', multiplier: 2.5 },
    { value: 'villa', label: 'Villa/Large Home', multiplier: 3.5 },
  ];

  const specialItemsList = [
    { value: 'piano', label: 'Piano', icon: 'MusicalNoteIcon' },
    { value: 'artwork', label: 'Artwork/Antiques', icon: 'PaintBrushIcon' },
    { value: 'electronics', label: 'Large Electronics', icon: 'TvIcon' },
    { value: 'appliances', label: 'Heavy Appliances', icon: 'CubeIcon' },
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof QuoteFormData, string>> = {};

    if (step === 1) {
      if (!formData.moveType) newErrors.moveType = 'Please select a move type';
      if (!formData.moveDate) newErrors.moveDate = 'Please select a move date';
      if (!formData.fromLocation) newErrors.fromLocation = 'Please enter pickup location';
      if (!formData.toLocation) newErrors.toLocation = 'Please enter delivery location';
    }

    if (step === 2) {
      if (!formData.propertySize) newErrors.propertySize = 'Please select property size';
    }

    if (step === 3) {
      if (!formData.contactName) newErrors.contactName = 'Please enter your name';
      if (!formData.contactPhone) newErrors.contactPhone = 'Please enter your phone number';
      if (!formData.contactEmail) newErrors.contactEmail = 'Please enter your email';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
        newErrors.contactEmail = 'Please enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof QuoteFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSpecialItemToggle = (item: string) => {
    setFormData((prev) => ({
      ...prev,
      specialItems: prev.specialItems.includes(item)
        ? prev.specialItems.filter((i) => i !== item)
        : [...prev.specialItems, item],
    }));
  };

  const calculateQuote = () => {
    setIsCalculating(true);

    setTimeout(() => {
      const basePrice = 50;
      const sizeMultiplier = propertySizes.find((s) => s.value === formData.propertySize)?.multiplier || 1;
      const distance = Math.floor(Math.random() * 30) + 5;
      const distancePrice = distance * 2;
      const packingPrice = formData.packingService ? 80 : 0;
      const assemblyPrice = formData.assemblyService ? 60 : 0;
      const storagePrice = formData.storageService ? 100 : 0;
      const specialItemsPrice = formData.specialItems.length * 30;

      const estimatedPrice =
        (basePrice + distancePrice) * sizeMultiplier +
        packingPrice +
        assemblyPrice +
        storagePrice +
        specialItemsPrice;

      onQuoteGenerated({
        ...formData,
        estimatedPrice: Math.round(estimatedPrice),
        distance,
      });

      setIsCalculating(false);
    }, 1500);
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 3) {
        calculateQuote();
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-soft p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-soft p-6 lg:p-8">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-headline font-bold transition-all duration-base ${
                  currentStep >= step
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-text-secondary'
                }`}
              >
                {step}
              </div>
              {step < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-all duration-base ${
                    currentStep > step ? 'bg-primary' : 'bg-muted'
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm font-body">
          <span className={currentStep >= 1 ? 'text-primary font-semibold' : 'text-text-secondary'}>
            Move Details
          </span>
          <span className={currentStep >= 2 ? 'text-primary font-semibold' : 'text-text-secondary'}>
            Services
          </span>
          <span className={currentStep >= 3 ? 'text-primary font-semibold' : 'text-text-secondary'}>
            Contact Info
          </span>
        </div>
      </div>

      {/* Step 1: Move Details */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-headline font-bold text-text-primary mb-4">
              Move Details
            </h3>
            <p className="text-text-secondary font-body mb-6">
              Tell us about your move to get an accurate estimate
            </p>
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-3">
              Move Type *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {moveTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleInputChange('moveType', type.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-base flex flex-col items-center space-y-2 ${
                    formData.moveType === type.value
                      ? 'border-primary bg-primary bg-opacity-5' :'border-border hover:border-primary hover:bg-muted'
                  }`}
                >
                  <Icon
                    name={type.icon as any}
                    size={32}
                    variant={formData.moveType === type.value ? 'solid' : 'outline'}
                    className={formData.moveType === type.value ? 'text-primary' : 'text-text-secondary'}
                  />
                  <span className="font-body font-medium text-text-primary">{type.label}</span>
                </button>
              ))}
            </div>
            {errors.moveType && (
              <p className="text-error text-sm font-body mt-2">{errors.moveType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-2">
              Move Date *
            </label>
            <input
              type="date"
              value={formData.moveDate}
              onChange={(e) => handleInputChange('moveDate', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-base font-body ${
                errors.moveDate
                  ? 'border-error focus:border-error' :'border-border focus:border-primary'
              } focus:outline-none`}
            />
            {errors.moveDate && (
              <p className="text-error text-sm font-body mt-2">{errors.moveDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-2">
              Pickup Location *
            </label>
            <div className="relative">
              <Icon
                name="MapPinIcon"
                size={20}
                variant="outline"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={formData.fromLocation}
                onChange={(e) => handleInputChange('fromLocation', e.target.value)}
                placeholder="Enter pickup address in Farwaniya"
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-all duration-base font-body ${
                  errors.fromLocation
                    ? 'border-error focus:border-error' :'border-border focus:border-primary'
                } focus:outline-none`}
              />
            </div>
            {errors.fromLocation && (
              <p className="text-error text-sm font-body mt-2">{errors.fromLocation}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-2">
              Delivery Location *
            </label>
            <div className="relative">
              <Icon
                name="MapPinIcon"
                size={20}
                variant="solid"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={formData.toLocation}
                onChange={(e) => handleInputChange('toLocation', e.target.value)}
                placeholder="Enter delivery address"
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-all duration-base font-body ${
                  errors.toLocation
                    ? 'border-error focus:border-error' :'border-border focus:border-primary'
                } focus:outline-none`}
              />
            </div>
            {errors.toLocation && (
              <p className="text-error text-sm font-body mt-2">{errors.toLocation}</p>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Services */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-headline font-bold text-text-primary mb-4">
              Property Size & Services
            </h3>
            <p className="text-text-secondary font-body mb-6">
              Select your property size and additional services
            </p>
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-3">
              Property Size *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {propertySizes.map((size) => (
                <button
                  key={size.value}
                  type="button"
                  onClick={() => handleInputChange('propertySize', size.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-base ${
                    formData.propertySize === size.value
                      ? 'border-primary bg-primary bg-opacity-5' :'border-border hover:border-primary hover:bg-muted'
                  }`}
                >
                  <span className="font-body font-medium text-text-primary block">{size.label}</span>
                </button>
              ))}
            </div>
            {errors.propertySize && (
              <p className="text-error text-sm font-body mt-2">{errors.propertySize}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-3">
              Additional Services
            </label>
            <div className="space-y-3">
              <label className="flex items-center p-4 rounded-lg border-2 border-border hover:border-primary transition-all duration-base cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.packingService}
                  onChange={(e) => handleInputChange('packingService', e.target.checked)}
                  className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center space-x-2">
                    <Icon name="ArchiveBoxIcon" size={20} variant="outline" className="text-primary" />
                    <span className="font-body font-semibold text-text-primary">
                      Professional Packing
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary font-body mt-1">
                    Complete packing service with quality materials (+80 KWD)
                  </p>
                </div>
              </label>

              <label className="flex items-center p-4 rounded-lg border-2 border-border hover:border-primary transition-all duration-base cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.assemblyService}
                  onChange={(e) => handleInputChange('assemblyService', e.target.checked)}
                  className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center space-x-2">
                    <Icon name="WrenchScrewdriverIcon" size={20} variant="outline" className="text-primary" />
                    <span className="font-body font-semibold text-text-primary">
                      Furniture Assembly
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary font-body mt-1">
                    Disassembly and reassembly of furniture (+60 KWD)
                  </p>
                </div>
              </label>

              <label className="flex items-center p-4 rounded-lg border-2 border-border hover:border-primary transition-all duration-base cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.storageService}
                  onChange={(e) => handleInputChange('storageService', e.target.checked)}
                  className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center space-x-2">
                    <Icon name="BuildingStorefrontIcon" size={20} variant="outline" className="text-primary" />
                    <span className="font-body font-semibold text-text-primary">
                      Temporary Storage
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary font-body mt-1">
                    Secure storage for up to 30 days (+100 KWD)
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-3">
              Special Items (Optional)
            </label>
            <div className="grid grid-cols-2 gap-3">
              {specialItemsList.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleSpecialItemToggle(item.value)}
                  className={`p-3 rounded-lg border-2 transition-all duration-base flex items-center space-x-2 ${
                    formData.specialItems.includes(item.value)
                      ? 'border-primary bg-primary bg-opacity-5' :'border-border hover:border-primary hover:bg-muted'
                  }`}
                >
                  <Icon
                    name={item.icon as any}
                    size={20}
                    variant={formData.specialItems.includes(item.value) ? 'solid' : 'outline'}
                    className={
                      formData.specialItems.includes(item.value) ? 'text-primary' : 'text-text-secondary'
                    }
                  />
                  <span className="font-body text-sm font-medium text-text-primary">{item.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-text-secondary font-body mt-2">
              Each special item adds 30 KWD for extra care and handling
            </p>
          </div>
        </div>
      )}

      {/* Step 3: Contact Info */}
      {currentStep === 3 && (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-headline font-bold text-text-primary mb-4">
              Contact Information
            </h3>
            <p className="text-text-secondary font-body mb-6">
              How should we reach you with your quote?
            </p>
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-2">
              Full Name *
            </label>
            <div className="relative">
              <Icon
                name="UserIcon"
                size={20}
                variant="outline"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => handleInputChange('contactName', e.target.value)}
                placeholder="Enter your full name"
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-all duration-base font-body ${
                  errors.contactName
                    ? 'border-error focus:border-error' :'border-border focus:border-primary'
                } focus:outline-none`}
              />
            </div>
            {errors.contactName && (
              <p className="text-error text-sm font-body mt-2">{errors.contactName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-2">
              Phone Number *
            </label>
            <div className="relative">
              <Icon
                name="PhoneIcon"
                size={20}
                variant="outline"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                placeholder="Enter your number"
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-all duration-base font-body ${
                  errors.contactPhone
                    ? 'border-error focus:border-error' :'border-border focus:border-primary'
                } focus:outline-none`}
              />
            </div>
            {errors.contactPhone && (
              <p className="text-error text-sm font-body mt-2">{errors.contactPhone}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-body font-semibold text-text-primary mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Icon
                name="EnvelopeIcon"
                size={20}
                variant="outline"
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                placeholder="your.email@example.com"
                className={`w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-all duration-base font-body ${
                  errors.contactEmail
                    ? 'border-error focus:border-error' :'border-border focus:border-primary'
                } focus:outline-none`}
              />
            </div>
            {errors.contactEmail && (
              <p className="text-error text-sm font-body mt-2">{errors.contactEmail}</p>
            )}
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="InformationCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
              <div>
                <p className="text-sm font-body text-text-primary">
                  Your quote will be sent to your email and we&apos;ll call you within 30 minutes to confirm details.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
        {currentStep > 1 && (
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center space-x-2 px-6 py-3 rounded-lg border-2 border-border hover:border-primary hover:bg-muted transition-all duration-base font-body font-semibold text-text-primary"
          >
            <Icon name="ArrowLeftIcon" size={20} variant="outline" />
            <span>Back</span>
          </button>
        )}
        <button
          type="button"
          onClick={handleNext}
          disabled={isCalculating}
          className={`flex items-center space-x-2 px-8 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-opacity-90 transition-all duration-base font-body font-semibold shadow-soft ${
            currentStep === 1 ? 'ml-auto' : ''
          } ${isCalculating ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span>{currentStep === 3 ? (isCalculating ? 'Calculating...' : 'Get Quote') : 'Next'}</span>
          {!isCalculating && <Icon name="ArrowRightIcon" size={20} variant="outline" />}
        </button>
      </div>
    </div>
  );
};

export default QuoteForm;